import React, { useEffect, useState } from 'react';
import { formatDateTime, isNullOrEmptyString } from '../../services/UtilityService';
import CustomTable from '../../share-components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { ROUTE_ADD_SHOP } from '../../constants/routeConstants';
import { Button, Card, Container } from 'react-bootstrap';
import CommonDialog from '../../share-components/CommonDialog';
import * as Yup from 'yup';
import { useAppContext } from '../../share-components/AppContext';
import { ErrorMessage, Field, Form, Formik } from 'formik/dist';
import AlertDialog from '../../share-components/AlertDialog';
import { deleteShop, editShop, getAllShops } from '../../services/ShopService';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import FormTitle from '../../share-components/FormTitle';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';
import NoDataFound from '../../share-components/NoDataFound';

const ShopList = () => {
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [shopListData, setShopListData] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showEditShopForm, setShowEditShopForm] = useState(false);
    const [showDeleteShopConfirmDialog, setShowDeleteShopConfirmDialog] = useState(false);
    const [actionTargetShop, setActionTargetShop] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const navigate = useNavigate();

    const fetchShopList = async () => {
        setLoading(true);
        try {
            const response = await getAllShops();
            const allShops = response?.allShops || [];
            if (allShops) {
                const tableData = allShops.map((shop) => ({
                    ...shop,
                    createdDateTimeShow: formatDateTime(shop.createdDateTime?.slice(0, 8)),
                    updatedDateTimeShow: formatDateTime(shop.updatedDateTime?.slice(0, 8))
                }));
                setShopListData(tableData);
            }
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("MANAGEMENT >> SHOP LIST");
        fetchShopList();
    }, []);

    const handleEditShopClick = (shop) => {
        setActionTargetShop(shop);
        setShowEditShopForm(true);
    }

    const handleDeleteShopClick = (shop) => {
        setActionTargetShop(shop);
        setShowDeleteShopConfirmDialog(true);
    }

    const columns = [
        {
            Header: 'Shop Name',
            accessor: 'shopName'
        },
        {
            Header: 'Created Date',
            accessor: 'createdDateTimeShow',
        },
        {
            Header: 'Updated Date',
            accessor: 'updatedDateTimeShow',
        },
        {
            Header: 'Edit Shop',
            accessor: 'editShop',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            Cell: ({ row }) => (
                <Button variant='secondary'
                    onClick={() => handleEditShopClick(row.original)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-pencil-square"></i>
                </Button>
            )
        },
        {
            Header: 'Delete Shop',
            accessor: 'deleteShop',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            Cell: ({ row }) => (
                <Button variant='danger'
                    onClick={() => handleDeleteShopClick(row.original)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-trash3-fill"></i>
                </Button>
            )
        }
    ];

    const goToAddShop = () => {
        navigate(ROUTE_ADD_SHOP);
    }

    const initialValues = {
        shopName: "",
    };

    const validationSchema = Yup.object({
        shopName: Yup.string()
            .max(50, 'Shop Name must not exceed 50 characters.')
            .required('Shop Name is required.'),
    });

    const initialValuesForDeleteShop = {
        password: "",
    };

    const validationSchemaForDeleteShop = Yup.object({
        password: Yup.string()
            .required('Password is required.'),
    });

    const onSubmit = async (form) => {
        setLoading(true);
        try {
            const requestData = {
                shopId: actionTargetShop?.shopId,
                shopName: form.shopName
            }
            await editShop(requestData);
            setSuccessMessage("Shop is Successfully Edited.")
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteShop = async (form) => {
        setLoading(true);
        try {
            const requestData = {
                shopId: actionTargetShop?.shopId,
                password: form.password,
            }
            await deleteShop(requestData);
            setSuccessMessage(`Shop is Successfully Deleted.`);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowEditShopForm(false);
        setShowDeleteShopConfirmDialog(false);
        setSuccessMessage(null);
        setActionTargetShop(null);
        await fetchShopList();
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const renderEditShop = (
        <Container style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '30px', fontWeight: 'normal' }}>

            {/* ShopEdit Form */}
            <Card style={{ width: '100%', maxWidth: '400px', padding: '20px', margin: '0 auto' }}>
                <Card.Body>
                    <FormTitle title={"Edit Shop Name"} isCenter={true} />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                        enableReinitialize={true}>
                        {({ errors, isValid, dirty }) => {
                            return (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Old Shop Name</label>
                                        <div className="w-100 label-box">
                                            {actionTargetShop?.shopName}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">New Shop Name*</label>
                                        <Field name="shopName" type='text' className="form-control customInputControl" />
                                        <ErrorMessage name="shopName" component="div" className="text-danger" />
                                    </div>

                                    <Button
                                        className="w-100 mb-3"
                                        type="submit"
                                        variant="primary"
                                        disabled={!(isValid && dirty)}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        className="w-100 mb-3"
                                        type="button"
                                        variant="secondary"
                                        onClick={() => { setShowEditShopForm(false); setActionTargetShop(null) }}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );

    const renderDeleteShop = (
        <Container style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '30px', fontWeight: 'normal' }}>

            {/* Shop Delete Form */}
            <Card style={{ width: '100%', maxWidth: '400px', padding: '20px', margin: '0 auto' }}>
                <Card.Body>
                    <FormTitle title={"Delete Shop"} isCenter={true} />
                    <Formik
                        initialValues={initialValuesForDeleteShop}
                        validationSchema={validationSchemaForDeleteShop}
                        onSubmit={handleDeleteShop}
                        enableReinitialize={true}>
                        {({ errors, isValid, dirty }) => {
                            return (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Shop Name</label>
                                        <div className="w-100 label-box">
                                            {actionTargetShop?.shopName}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Password*</label>
                                        <Field name="password" type={showPassword ? 'text' : 'password'} className="form-control customInputControl" />
                                        <ErrorMessage name="password" component="div" className="text-danger" />
                                        <div style={{
                                            color: 'GrayText',
                                            fontSize: 'small'
                                        }}>Enter your account password to delete shop!</div>
                                    </div>
                                    {/* Show Password Checkbox */}
                                    <div className="mb-3">
                                        <label className='custom-label'>
                                            <input
                                                type="checkbox"
                                                checked={showPassword}
                                                onChange={togglePasswordVisibility}
                                            />
                                            &nbsp;Show Password
                                        </label>
                                    </div>

                                    <Button
                                        className="w-100 mb-3"
                                        type="submit"
                                        variant="danger"
                                        disabled={!(isValid && dirty)}
                                    >
                                        Delete
                                    </Button>
                                    <Button
                                        className="w-100 mb-3"
                                        type="button"
                                        variant="secondary"
                                        onClick={() => { setShowDeleteShopConfirmDialog(false); setActionTargetShop(null) }}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );

    return (
        <div id='shopListPage'>
            <FormTitle title={"Shop List"} isCenter={true} />
            {isFirstLoaded && <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                <div style={{ minWidth: "max-content", maxWidth: '650px', margin: '0 auto' }}>
                    <div style={{ display: 'inline-block' }}>
                        <Button type='button' variant='warning' className='btn-sm' onClick={goToAddShop}><i className="bi bi-plus-circle"></i> Add New Shop</Button>
                    </div>
                    <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>

                        <div style={{ display: 'inline-block' }}>
                            <SearchColumnSelect data={shopListData} setData={setFilteredData} columns={columns} />
                        </div>

                    </div>
                    {filteredData?.length > 0 ?
                        <CustomTable
                            tableId='shopListTbl'
                            columns={columns}
                            tblData={filteredData || []}
                            showPagination={true}
                            itemsPerPage={20}
                            initialSortBy={[{ id: 'shopName', desc: false }]}
                        /> : <NoDataFound />}
                </div>
                <CommonDialog
                    dialogId='shopEditDialog'
                    show={showEditShopForm}
                    noCloseBtn={false}
                    onHide={() => { setShowEditShopForm(false); setActionTargetShop(null); }}
                    size={'fullscreen'}
                    body={renderEditShop}
                />
                <CommonDialog
                    dialogId='deleteShopDialog'
                    show={showDeleteShopConfirmDialog}
                    noCloseBtn={false}
                    onHide={() => { setShowDeleteShopConfirmDialog(false); setActionTargetShop(null); }}
                    size={'fullscreen'}
                    body={renderDeleteShop}
                />
                <AlertDialog
                    dialogId={'successAlertDialog'}
                    showDialog={!isNullOrEmptyString(successMessage)}
                    body={successMessage}
                    onConfirm={handleSuccessDialogClose}
                />
            </div>}
        </div>
    );
};

export default ShopList;